/**
 * Capacité d'accueil d'une équipe : locaux, équipements et outils.
 *
 * Reprend fidèlement le calcul déjà appliqué à l'embauche et à la vente — postes comptés
 * par type pour les locaux, par type et par niveau pour les équipements, un collaborateur
 * pouvant occuper un poste de son niveau requis ou d'un niveau supérieur.
 *
 * Ce module ne décide rien : il dit ce qui manque. Décider d'autoriser ou non l'action
 * appartient à l'écran, et depuis la mise en place des alertes plus rien n'est refusé.
 *
 * Ce qui est mis en vente ou dont le bail est résilié ne compte plus : la capacité
 * mesurée est celle dont l'équipe disposera après application de sa stratégie, sans quoi
 * se séparer d'un élément indispensable ne déclencherait aucune alerte.
 */
const MAX_LEVEL = 11

/** Éléments retirés par la stratégie : mise en vente ou résiliation de bail. */
function removedUuids(strategyDomain) {
  return new Set((strategyDomain && strategyDomain.sell) || [])
}

/** Postes de locaux par type, achats de la période compris. */
export function buildingSpace({ strategyBuildings, previousStateBuildings, buildingsTable }) {
  const space = {}
  const removed = removedUuids(strategyBuildings)

  const add = buildingId => {
    const asset = buildingsTable[buildingId]
    if (!asset) return
    if (space[asset.type] === undefined) space[asset.type] = 0
    space[asset.type] += parseInt(asset.staffAmount) || 0
  }

  for (const instance of (strategyBuildings && strategyBuildings.buy) || []) {
    if (!removed.has(instance.uuid)) add(instance.building)
  }
  for (const key of Object.keys((previousStateBuildings && previousStateBuildings.items) || {})) {
    if (!removed.has(key)) add(previousStateBuildings.items[key].building)
  }

  return space
}

/** Postes d'équipement par type et par niveau, achats de la période compris. */
export function stuffSpace({ strategyStuffs, previousStateStuffs, stuffsTable }) {
  const space = {}
  const removed = removedUuids(strategyStuffs)

  const add = stuffId => {
    const asset = stuffsTable[stuffId]
    if (!asset) return
    if (space[asset.type] === undefined) space[asset.type] = new Array(MAX_LEVEL).fill(0)
    space[asset.type][parseInt(asset.level) || 0] += parseInt(asset.posts) || 0
  }

  for (const instance of (strategyStuffs && strategyStuffs.buy) || []) {
    if (!removed.has(instance.uuid)) add(instance.stuff)
  }
  for (const key of Object.keys((previousStateStuffs && previousStateStuffs.items) || {})) {
    if (!removed.has(key)) add(previousStateStuffs.items[key].stuff)
  }

  return space
}

/**
 * Outils détenus, comptés par type et par niveau.
 *
 * Contrairement aux postes, un outil n'est pas « occupé » par un collaborateur : le moteur
 * raisonne en temps d'utilisation. On se contente donc ici de savoir si l'équipe possède
 * au moins un outil du type et du niveau demandés.
 */
export function toolStock({ strategyTools, previousStateTools, toolsTable }) {
  const stock = {}
  const removed = removedUuids(strategyTools)

  const add = toolId => {
    const asset = toolsTable[toolId]
    if (!asset) return
    if (stock[asset.type] === undefined) stock[asset.type] = new Array(MAX_LEVEL).fill(0)
    stock[asset.type][parseInt(asset.level) || 0] += 1
  }

  for (const instance of (strategyTools && strategyTools.buy) || []) {
    if (!removed.has(instance.uuid)) add(instance.tool)
  }
  for (const key of Object.keys((previousStateTools && previousStateTools.items) || {})) {
    if (!removed.has(key)) add(previousStateTools.items[key].tool)
  }

  return stock
}

/** Occupe un poste de niveau au moins égal à celui demandé. Renvoie `false` si aucun. */
function takeLevel(levels, requiredLevel) {
  if (!levels) return false
  for (let level = parseInt(requiredLevel) || 0; level < levels.length; level++) {
    if (levels[level] > 0) {
      levels[level]--
      return true
    }
  }
  return false
}

/** Un niveau au moins égal existe-t-il, sans le consommer. */
export function hasLevel(levels, requiredLevel) {
  if (!levels) return false
  for (let level = parseInt(requiredLevel) || 0; level < levels.length; level++) {
    if (levels[level] > 0) return true
  }
  return false
}

/** Incrémente le compteur de manques pour un type donné. */
function countMissing(counter, type) {
  counter.set(type, (counter.get(type) || 0) + 1)
}

/** Un manque vide, prêt à être rempli. */
function emptyMissing() {
  // Tout se compte de la même façon : le nombre de collaborateurs laissés sans place,
  // sans poste équipé ou sans outil. C'est ce nombre que l'équipe doit combler.
  return { buildings: new Map(), stuffs: new Map(), tools: new Map() }
}

/**
 * Consomme la capacité pour un ensemble de collaborateurs et renvoie ce qui reste,
 * ainsi que les manques rencontrés en chemin, quantifiés par type.
 *
 * @param {object[]} staffList collaborateurs, chacun portant l'identifiant de son métier
 * @returns {{buildings: object, stuffs: object, tools: object, missing: object}}
 */
export function consume({ staffList, jobsTable, space }) {
  const buildings = JSON.parse(JSON.stringify(space.buildings))
  const stuffs = JSON.parse(JSON.stringify(space.stuffs))
  const tools = space.tools

  const missing = emptyMissing()
  // Le détail par collaborateur, en plus des totaux : un même équipement sert à plusieurs
  // métiers, et « il manque 2 ordinateurs » ne dit pas qui attend quoi. C'est ce détail
  // que les alertes regroupent par métier.
  const shortages = []

  for (const staff of staffList) {
    const job = jobsTable[staff.job]
    if (!job || !Array.isArray(job.requirements)) continue

    const shortage = { job: staff.job, buildings: [], stuffs: [], tools: [] }

    for (const requirement of job.requirements) {
      if (requirement.type === 'buildingTypes') {
        if (buildings[requirement.object] > 0) buildings[requirement.object]--
        else {
          countMissing(missing.buildings, requirement.object)
          shortage.buildings.push(requirement.object)
        }
      } else if (requirement.type === 'stuffTypes') {
        if (!takeLevel(stuffs[requirement.object], requirement.level)) {
          countMissing(missing.stuffs, requirement.object)
          shortage.stuffs.push(requirement.object)
        }
      } else if (requirement.type === 'toolTypes') {
        // L'outil n'est pas un poste : il n'est pas consommé, mais chaque collaborateur
        // qui en a besoin sans qu'il existe est compté.
        if (!hasLevel(tools[requirement.object], requirement.level)) {
          countMissing(missing.tools, requirement.object)
          shortage.tools.push(requirement.object)
        }
      }
    }

    if (shortage.buildings.length || shortage.stuffs.length || shortage.tools.length) {
      shortages.push(shortage)
    }
  }

  return { buildings, stuffs, tools, missing, shortages }
}

/**
 * Ce qui manquerait pour accueillir un collaborateur de plus, l'effectif déjà retenu
 * pour la période étant servi en premier.
 *
 * @returns {{buildings: Map, stuffs: Map, tools: Map}} manques quantifiés par type,
 *   vides si tout est disponible
 */
export function missingForStaff({ candidateJobId, staffList, jobsTable, space }) {
  const remaining = consume({ staffList, jobsTable, space })
  const job = jobsTable[candidateJobId]

  const missing = emptyMissing()
  if (!job || !Array.isArray(job.requirements)) return missing

  for (const requirement of job.requirements) {
    if (requirement.type === 'buildingTypes') {
      if (remaining.buildings[requirement.object] > 0) remaining.buildings[requirement.object]--
      else countMissing(missing.buildings, requirement.object)
    } else if (requirement.type === 'stuffTypes') {
      if (!takeLevel(remaining.stuffs[requirement.object], requirement.level)) {
        countMissing(missing.stuffs, requirement.object)
      }
    } else if (requirement.type === 'toolTypes') {
      if (!hasLevel(remaining.tools[requirement.object], requirement.level)) {
        countMissing(missing.tools, requirement.object)
      }
    }
  }

  return missing
}

export default { buildingSpace, stuffSpace, toolStock, hasLevel, consume, missingForStaff }
